import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import MapView from "@/components/MapView";
import api from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import RatingModal from "@/components/RatingModal";
import OrderChat from "@/components/OrderChat";
import MissedCallBanner from "@/components/MissedCallBanner";
import { useVoice } from "@/contexts/VoiceCallContext";
import { maskPhone } from "@/utils/maskPhone";
import { Check, Clock, ChefHat, PackageCheck, Bike, MapPin, Phone, PhoneCall, Home, Radio, WifiOff, Star } from "lucide-react";

const STEPS = [
  { key: "pending",   label: "استلام الطلب",       icon: Check },
  { key: "preparing", label: "قيد التحضير",         icon: ChefHat },
  { key: "ready",     label: "جاهز للتوصيل",        icon: PackageCheck },
  { key: "assigned",  label: "تم تعيين السائق",     icon: Bike },
  { key: "picked_up", label: "في الطريق إليك",      icon: MapPin },
  { key: "delivered", label: "تم التسليم",         icon: Home },
];

function statusIndex(s) {
  const i = STEPS.findIndex((x) => x.key === s);
  return i < 0 ? 0 : i;
}

// Fallback restaurant location if order has no branch snapshot (legacy)
const DEFAULT_REST_LOC = { lat: 32.0665, lng: 12.5303 };

export default function Tracking() {
  const { id } = useParams();
  const voice = useVoice();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [liveDriverLoc, setLiveDriverLoc] = useState(null);
  const [liveEta, setLiveEta] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const autoPromptedRef = useRef(false);

  // Auto-open the rating modal once when the order first shows as delivered and unrated.
  useEffect(() => {
    if (!order || autoPromptedRef.current) return;
    if (order.status === "delivered" && !order.rating_stars && !order.customer_rated) {
      autoPromptedRef.current = true;
      setShowRating(true);
    }
  }, [order]);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      if (data.driver?.location) setLiveDriverLoc(data.driver.location);
      if (data.route_meta) setLiveEta(data.route_meta);
    } catch (e) {
      setErr("لم نتمكن من جلب بيانات الطلب");
    }
  }, [id]);

  const { status: wsStatus, subscribe } = useWebSocket({
    onMessage: (msg) => {
      if (msg.event === "auth.ok" || msg.event === "subscribed") return;
      if (msg.event === "driver.location" && msg.data?.lat != null) {
        setLiveDriverLoc({ lat: msg.data.lat, lng: msg.data.lng });
        if (msg.data.eta) setLiveEta(msg.data.eta);
        return;
      }
      // Restaurant flagged a line-item as غير متوفر — surface a prominent
      // toast so the customer sees it even if they're not looking at the
      // item list. If the customer paid via wallet/mypay we also confirm
      // the auto-refund amount that just landed in their wallet. The full
      // fetch below then re-renders with the strike-through applied.
      if (msg.event === "order.item_unavailable") {
        const name = msg.data?.unavailable_item_name;
        const amt = Number(msg.data?.refund_amount || 0);
        const credited = !!msg.data?.wallet_credited;
        if (name) {
          if (credited && amt > 0) {
            toast.error(`⚠️ الصنف "${name}" غير متوفر — تم إرجاع ${amt.toFixed(2)} د.ل لمحفظتك`, { duration: 8000 });
          } else if (amt > 0) {
            toast.error(`⚠️ الصنف "${name}" غير متوفر — تم خصم ${amt.toFixed(2)} د.ل من إجمالي الطلب`, { duration: 8000 });
          } else {
            toast.error(`⚠️ الصنف "${name}" غير متوفر لدى المطعم`, { duration: 7000 });
          }
        }
      }
      // Restaurant proposed a replacement — refetch the order so the
      // suggestions card appears; the fetch below handles it.
      if (msg.event === "order.alternatives_suggested") {
        const name = msg.data?.suggestion?.unavailable_item_name;
        if (name) toast(`💡 المطعم اقترح بديلاً عن "${name}"`, { duration: 6000 });
      }
      // Any order.* event → refetch the full order for latest state
      if (msg.event?.startsWith("order.")) {
        fetchOrder();
      }
    },
  });

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (wsStatus === "open") subscribe([`order:${id}`]);
  }, [wsStatus, id, subscribe]);

  // Fallback: slow polling only if WS is not open
  useEffect(() => {
    if (wsStatus === "open") return;
    const t = setInterval(fetchOrder, 15000);
    return () => clearInterval(t);
  }, [wsStatus, fetchOrder]);

  if (err) return (
    <div className="min-h-screen bg-[var(--background)]"><TopBar/>
      <div className="max-w-2xl mx-auto p-6 text-center text-red-600">{err}</div></div>
  );
  if (!order) return (
    <div className="min-h-screen bg-[var(--background)]"><TopBar/>
      <div className="max-w-2xl mx-auto p-6"><div className="h-96 rounded-3xl shimmer"/></div></div>
  );

  const idx = statusIndex(order.status);
  const isDone = order.status === "delivered";
  const isCancelled = order.status === "cancelled";
  const driverLoc = liveDriverLoc || order.driver?.location;
  const custLoc = order.address ? { lat: order.address.lat, lng: order.address.lng } : null;
  const branchLoc = order.restaurant ? { lat: order.restaurant.lat, lng: order.restaurant.lng } : DEFAULT_REST_LOC;

  // ETA: use live route_meta from OSRM; fallback to linear estimate
  const etaSeconds = liveEta?.duration_s;
  const etaMinutes = etaSeconds != null ? Math.max(1, Math.round(etaSeconds / 60)) : Math.max(5, 35 - idx * 6);
  const etaKm = liveEta?.distance_m != null ? (liveEta.distance_m / 1000).toFixed(1) : null;
  const etaPhase = liveEta?.phase;

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-[var(--text-secondary)] uppercase tracking-widest font-bold">رقم الطلب</div>
            <h1 className="text-2xl font-black" style={{fontFamily:'Cairo'}} data-testid="order-code">{order.code}</h1>
          </div>
          <div className={`px-4 py-2 rounded-full font-bold text-sm ${isDone ? "bg-green-100 text-green-700" : isCancelled ? "bg-red-100 text-red-700" : "bg-primary-light text-primary-brand"}`} data-testid="order-status">
            {STEPS.find((s) => s.key === order.status)?.label || order.status}
          </div>
        </div>

        {/* Live WS badge */}
        {!isDone && !isCancelled && (
          <div className={`flex items-center gap-2 text-xs mb-3 ${wsStatus === "open" ? "text-green-600" : "text-[var(--text-secondary)]"}`} data-testid="ws-status">
            {wsStatus === "open" ? (
              <><Radio size={12} className="animate-pulse-dot"/> تتبع مباشر · تحديث فوري</>
            ) : (
              <><WifiOff size={12}/> اتصال غير متزامن · تحديث كل 15 ثانية</>
            )}
          </div>
        )}

        {/* Cancel actions (T&C §5) */}
        {!isDone && !isCancelled && (
          <CustomerCancelBar order={order} onDone={fetchOrder}/>
        )}

        {/* Map (only for delivery) */}
        {order.fulfillment === "delivery" && (
          <div className="rounded-3xl overflow-hidden talabya-shadow-soft border border-[var(--border)] mb-4 relative">
            <div className="h-72 sm:h-96">
              <MapView
                restaurant={[branchLoc.lat, branchLoc.lng]}
                driver={driverLoc ? [driverLoc.lat, driverLoc.lng] : null}
                customer={custLoc ? [custLoc.lat, custLoc.lng] : null}
              />
            </div>
            {!isDone && !isCancelled && (
              <div className="absolute top-4 right-4 bg-white px-4 py-2.5 rounded-2xl talabya-shadow-soft flex items-center gap-2 z-10">
                <Clock size={16} className="text-primary-brand"/>
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)]">
                    {liveEta ? "الوقت المتبقي (طريق حقيقي)" : "الوقت التقريبي"}
                    {etaPhase === "assigned" && " · للاستلام من المحل"}
                    {etaPhase === "picked_up" && " · للوصول إليك"}
                  </div>
                  <div className="font-black text-primary-brand flex items-baseline gap-1" style={{fontFamily:'Cairo'}} data-testid="eta">
                    {etaMinutes} <span className="text-xs font-bold">دقيقة</span>
                    {etaKm && <span className="text-xs text-[var(--text-secondary)] font-bold">· {etaKm} كم</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white rounded-3xl p-6 talabya-shadow-soft mb-4">
          <h2 className="text-lg font-bold mb-4" style={{fontFamily:'Cairo'}}>حالة الطلب</h2>
          <div className="flex flex-col gap-0 relative pt-2">
            <div className="absolute top-4 bottom-4 right-[19px] w-0.5 bg-[var(--border)] -z-0"/>
            {STEPS.filter((s) => order.fulfillment !== "pickup" || !["assigned", "picked_up"].includes(s.key)).map((s, i) => {
              const done = i <= idx;
              const active = i === idx && !isCancelled;
              const Icon = s.icon;
              return (
                <div key={s.key} className={`flex gap-4 items-start relative mb-6 last:mb-0 ${done ? "opacity-100" : "opacity-40"}`} data-testid={`step-${s.key}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 shrink-0 ${done ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] text-[var(--text-secondary)] border border-[var(--border)]"} ${active ? "talabya-shadow-float animate-pulse-dot" : ""}`}>
                    <Icon size={18}/>
                  </div>
                  <div className="pt-2">
                    <div className="font-bold">{s.label}</div>
                    {order.status_history?.filter((h) => h.status === s.key).map((h, j) => (
                      <div key={j} className="text-xs text-[var(--text-secondary)] mt-0.5">
                        {new Date(h.at).toLocaleTimeString("ar-LY", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Missed call bubble */}
        <MissedCallBanner orderId={order.id}/>

        {/* Driver Card */}
        {order.driver && (
          <div className="bg-white rounded-3xl p-5 talabya-shadow-soft mb-4 flex items-center gap-4" data-testid="driver-card">
            <div className="w-16 h-16 rounded-full bg-primary-light flex items-center justify-center text-primary-brand text-2xl font-black" style={{fontFamily:'Cairo'}}>
              {order.driver.name?.[0]}
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg" style={{fontFamily:'Cairo'}}>{order.driver.name}</div>
              <div className="text-sm text-[var(--text-secondary)]">{order.driver.vehicle} · ⭐ {order.driver.rating}</div>
            </div>
            {order.driver && ["assigned", "picked_up"].includes(order.status) && (
              <div className="flex flex-col items-center gap-1 shrink-0" data-testid="driver-call-cluster">
                {order.driver.phone && (
                  <a
                    href={`tel:${order.driver.phone}`}
                    className="w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700"
                    aria-label="اتصال هاتفي مباشر بالسائق"
                    data-testid="direct-call-driver"
                    title="اتصال هاتفي مباشر بالسائق"
                  >
                    <Phone size={18}/>
                  </a>
                )}
                {order.driver.phone && (
                  <span className="text-[10px] font-black text-green-700 tracking-tight" data-testid="driver-phone-masked">
                    {maskPhone(order.driver.phone)}
                  </span>
                )}
                <button
                  onClick={() => voice.startOutgoing(order.id, { name: order.driver.name })}
                  disabled={voice.state !== "idle" && voice.state !== "ended" && voice.state !== "error"}
                  className="mt-0.5 px-2 py-1 rounded-full bg-primary-brand text-white flex items-center gap-1 text-[10px] font-bold hover:bg-blue-800 disabled:opacity-50"
                  aria-label="اتصال صوتي داخل التطبيق (بدون رصيد)"
                  data-testid="call-driver"
                  title="اتصال داخل التطبيق (بدون رصيد)"
                >
                  <PhoneCall size={12}/> داخل التطبيق
                </button>
              </div>
            )}
          </div>
        )}

        {/* Order details */}
        <div className="bg-white rounded-3xl p-5 talabya-shadow-soft">
          <div className="text-sm font-bold mb-3">تفاصيل الطلب</div>
          {order.restaurant && (
            <div className="mb-3 pb-3 border-b border-[var(--border)] flex items-center gap-2 text-sm" data-testid="order-restaurant">
              <MapPin size={14} className="text-primary-brand"/>
              <span className="font-bold">{order.restaurant.name}</span>
              <span className="text-[var(--text-secondary)]">·</span>
              <span className="text-[var(--text-secondary)] text-xs">{order.restaurant.address}</span>
            </div>
          )}
          {order.items.map((it, i) => (
            <div key={i} className={`flex justify-between py-1.5 text-sm ${it.unavailable ? "opacity-70" : ""}`}
                 data-testid={`track-item-${i}`}>
              <span className={it.unavailable ? "line-through text-red-500" : ""}>
                {it.name} × {it.quantity}
                {it.unavailable && (
                  <span className="mr-2 inline-block bg-red-100 text-red-700 text-[10px] font-black px-1.5 py-0.5 rounded-full no-underline"
                        data-testid={`track-item-unavailable-${i}`}>
                    غير متوفر
                  </span>
                )}
              </span>
              <span className={`text-[var(--text-secondary)] ${it.unavailable ? "line-through" : ""}`}>{it.line_total.toFixed(2)} د.ل</span>
            </div>
          ))}
          <div className="border-t border-[var(--border)] my-2"/>
          <div className="flex justify-between font-black text-primary-brand" style={{fontFamily:'Cairo'}}>
            <span>الإجمالي</span><span>{order.total.toFixed(2)} د.ل</span>
          </div>
        </div>

        {/* Restaurant-proposed replacements for an unavailable item. Only
            unresolved (status=pending) suggestions render — the customer can
            call/chat the driver to accept them (or ignore them). */}
        {Array.isArray(order.pending_suggestions) && order.pending_suggestions
          .filter((s) => (s.status || "pending") === "pending").length > 0 && (
          <div className="mt-4 space-y-3" data-testid="alt-suggestions">
            {order.pending_suggestions
              .filter((s) => (s.status || "pending") === "pending")
              .map((sug) => (
                <div key={sug.id} className="bg-emerald-50 border-2 border-emerald-200 rounded-3xl p-4"
                     data-testid={`alt-suggestion-${sug.id}`}>
                  <div className="flex items-center gap-2 text-emerald-700 text-[11px] font-black mb-1">
                    💡 المطعم يقترح بديلاً
                  </div>
                  <div className="text-sm mb-3">
                    الصنف <span className="font-black text-red-600 line-through">{sug.unavailable_item_name}</span> غير متوفر.
                    اختر بديلاً من الاقتراحات:
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(sug.suggestions || []).map((alt) => (
                      <div key={alt.menu_item_id}
                           className="bg-white rounded-2xl p-3 border border-emerald-100 flex items-center justify-between gap-2"
                           data-testid={`alt-item-${alt.menu_item_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-black text-sm truncate" style={{fontFamily:'Cairo'}}>{alt.name}</div>
                        </div>
                        <div className="shrink-0 text-primary-brand font-black text-sm">
                          {Number(alt.price || 0).toFixed(2)} د.ل
                        </div>
                      </div>
                    ))}
                  </div>
                  {sug.note && (
                    <div className="mt-2 text-xs text-emerald-800 bg-white/70 rounded-lg px-3 py-2">
                      📝 {sug.note}
                    </div>
                  )}
                  <div className="mt-3 text-[11px] text-emerald-800">
                    💬 للتأكيد أو الرفض — راسل السائق عبر المحادثة أدناه بعد قبوله الطلب.
                  </div>
                </div>
              ))}
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/orders" className="text-sm text-primary-brand font-bold hover:underline" data-testid="all-orders-link">عرض كل طلباتي</Link>
        </div>
      </div>

      {/* Order chat — shown once a driver is assigned */}
      {order?.driver_id && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-6">
          <OrderChat orderId={order.id} driverPhone={order?.driver?.phone}/>
        </div>
      )}

      {/* Rating CTA when delivered & not rated */}
      {isDone && !order.rating && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-auto z-30 animate-fade-up">
          <button onClick={() => setShowRating(true)}
            className="w-full sm:w-auto bg-primary-brand text-white px-6 py-4 rounded-2xl talabya-shadow-hover flex items-center justify-center gap-3 hover:opacity-95"
            data-testid="open-rating">
            <Star size={20} className="fill-white"/> قيّم تجربتك
          </button>
        </div>
      )}
      {isDone && order.rating && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-6">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3" data-testid="rated-badge">
            <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center"><Star size={18} className="fill-white"/></div>
            <div className="flex-1">
              <div className="font-bold text-green-800">شكراً لتقييمك!</div>
              {(order.rating.driver_stars || order.rating.food_stars) && (
                <div className="text-xs text-green-700 mt-0.5">
                  {order.rating.driver_stars && `السائق: ${'⭐'.repeat(order.rating.driver_stars)} · `}
                  {order.rating.food_stars && `الطعام: ${'⭐'.repeat(order.rating.food_stars)}`}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRating && (
        <RatingModal order={order} onClose={() => setShowRating(false)} onRated={fetchOrder}/>
      )}
    </div>
  );
}

// ────────── Customer cancel controls (T&C §5) ──────────
// If no driver has accepted: allow direct cancel with a reason.
// If a driver has accepted: open a "request cancellation" form that goes to admin.
function CustomerCancelBar({ order, onDone }) {
  const [mode, setMode] = useState(null); // null | "direct" | "request"
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const driverAccepted = Boolean(order.driver_id);

  const submit = async () => {
    setErr(""); setOk(""); setBusy(true);
    try {
      if (mode === "direct") {
        await api.post(`/orders/${order.id}/cancel`, { reason });
        setOk("تم إلغاء الطلب");
      } else if (mode === "request") {
        if (reason.trim().length < 3) { setErr("يرجى كتابة سبب واضح للإلغاء"); setBusy(false); return; }
        await api.post(`/orders/${order.id}/cancel-request`, { reason });
        setOk("تم إرسال طلبك إلى الإدارة، ستتلقى رداً قريباً");
      }
      setMode(null); setReason("");
      onDone?.();
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر تنفيذ الطلب"); }
    finally { setBusy(false); }
  };

  if (mode) {
    return (
      <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-200" data-testid="cancel-form">
        <div className="font-black text-sm mb-2 text-red-700">
          {mode === "direct" ? "إلغاء الطلب" : "طلب إلغاء (تراجع الإدارة)"}
        </div>
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="السبب (اختياري إذا لم يقبل السائق بعد)"
                  className="w-full px-3 py-2 rounded-xl border border-red-200 bg-white focus:border-red-400 focus:outline-none text-sm mb-2"
                  data-testid="cancel-reason"/>
        {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy}
                  className="flex-1 py-2 rounded-xl bg-red-600 text-white text-sm font-black hover:bg-red-700 disabled:opacity-60"
                  data-testid="cancel-submit">
            {busy ? "جاري..." : mode === "direct" ? "تأكيد الإلغاء" : "إرسال طلب الإلغاء"}
          </button>
          <button onClick={() => { setMode(null); setReason(""); setErr(""); }}
                  className="px-4 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-bold" data-testid="cancel-back">
            رجوع
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 flex flex-col gap-2" data-testid="cancel-bar">
      {ok && <div className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 rounded-lg">{ok}</div>}
      {!driverAccepted ? (
        <button onClick={() => setMode("direct")}
                className="w-full py-2.5 rounded-xl bg-white border-2 border-red-200 text-red-600 text-sm font-black hover:bg-red-50 flex items-center justify-center gap-1"
                data-testid="btn-cancel">
          ✕ إلغاء الطلب
        </button>
      ) : (
        <button onClick={() => setMode("request")}
                className="w-full py-2.5 rounded-xl bg-white border-2 border-amber-300 text-amber-700 text-sm font-black hover:bg-amber-50 flex items-center justify-center gap-1"
                data-testid="btn-request-cancel">
          ✕ طلب إلغاء (يحتاج موافقة الإدارة)
        </button>
      )}
      <div className="text-[10px] text-[var(--text-secondary)] text-center">
        {!driverAccepted
          ? "يمكنك الإلغاء مباشرةً — لم يقبل سائق طلبك بعد"
          : "بعد قبول السائق، الإلغاء يتطلب مراجعة الإدارة (المادة 5 من الشروط)"}
      </div>
    </div>
  );
}
